/**
 * PlaceholderModule — canonical `v0 · mock` shell for module-registry entries
 * whose real implementation hasn't landed yet.
 *
 * Cycle-109 v2 sprint-204 S2.3 (sprint-plan AC-2.3.a). Ports the canonical
 * primitive from `output/design_reference/design_handoff_echelon_2026_04_26/
 * workspace/moduleRegistry.jsx:16-42` into production TypeScript.
 *
 * Pure-render, no state. Mounted by `placeholderLazy()` factory in
 * `frontend/src/lib/moduleRegistry.ts` for the 11 canonical-only entries that
 * ship without a real component in sprint-204:
 *   * echelon.informs, echelon.desk, echelon.evidence, echelon.claims,
 *     echelon.paradox · geo.unrest · nat.disasters, nat.wildfires ·
 *     econ.predictions · infra.infrastructure · other.news, other.research,
 *     other.positive, other.giving
 *
 * Real components land in cycle-110+ (canonical-only IDs) or in sprint-204
 * S2.8/S2.9 (echelon.desk/evidence/claims real-component adapters).
 *
 * Supersedes `frontend/src/components/workspace/modules/ModulePlaceholder.tsx`
 * (cycle-101 sprint-3 era), which used a generic "lands in sprint-N" legend
 * shape that didn't match the canonical operator-facing layout.
 */

import type { ReactElement } from 'react';
import type { ModuleCategory } from '../../lib/moduleRegistry';

// ── Category visual mapping (canonical CATEGORIES export) ───────────────
//
// Per `output/design_reference/design_handoff_echelon_2026_04_26/workspace/
// moduleRegistry.jsx:48-55`. Colors map to design-system `--sig-*` tokens
// (legacy `--cd-sig-*` aliases preserved by `styles/legacy-aliases.css`,
// but new code targets the canonical names).

export interface CategoryVisual {
  readonly label: string;
  readonly color: string;
  readonly glyph: string;
}

export const CATEGORY_VISUAL: Record<ModuleCategory, CategoryVisual> = {
  echelon: { label: 'Echelon', color: 'var(--fg)', glyph: '◆' },
  geopolitical: {
    label: 'Geopolitical',
    color: 'var(--sig-iris)',
    glyph: '▲',
  },
  natural: {
    label: 'Natural events',
    color: 'var(--sig-amber)',
    glyph: '◉',
  },
  economy: {
    label: 'Economy & markets',
    color: 'var(--sig-ember)',
    glyph: '●',
  },
  infrastructure: {
    label: 'Infrastructure & transport',
    color: 'var(--sig-azure)',
    glyph: '■',
  },
  other: { label: 'Other', color: 'var(--sig-mint)', glyph: '◇' },
} as const;

// ── Props ───────────────────────────────────────────────────────────────

export interface PlaceholderModuleProps {
  /** Operator-facing module label — e.g. "Wildfire Activity". */
  readonly label: string;
  /** Canonical six-set category — drives kicker + color + glyph. */
  readonly category: ModuleCategory;
  /** Fine-grain subcategory — appears in kicker after CATEGORY · . */
  readonly subcategory: string;
  /** Operator-facing tooltip / picker subtitle. */
  readonly description: string;
}

// ── Component ───────────────────────────────────────────────────────────

export function PlaceholderModule({
  label,
  category,
  subcategory,
  description,
}: PlaceholderModuleProps): ReactElement {
  const visual = CATEGORY_VISUAL[category];
  const kicker = `${visual.label.toUpperCase()} · ${subcategory.toUpperCase()}`;
  return (
    <div
      role="region"
      aria-label={`${label} placeholder · v0 mock`}
      data-testid="placeholder-module"
      data-category={category}
      data-subcategory={subcategory}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        padding: '14px 16px',
        background: 'var(--bg-card, var(--cd-bg-card))',
        border: '1px solid var(--border, var(--cd-border))',
        borderRadius: 'var(--radius, var(--cd-radius))',
        fontFamily: 'var(--font-sans)',
        color: 'var(--fg, var(--cd-fg))',
        overflow: 'hidden',
      }}
    >
      {/* Header: kicker + v0 · mock tag + title row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            letterSpacing: '0.14em',
            color: 'var(--fg-dim, var(--cd-fg-muted))',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            aria-hidden="true"
            data-testid="placeholder-glyph"
            style={{
              color: visual.color,
              fontSize: 11,
              lineHeight: 1,
            }}
          >
            {visual.glyph}
          </span>
          {kicker}
        </span>
        <span
          className="mono"
          data-testid="placeholder-tag"
          style={{
            fontSize: 9,
            letterSpacing: '0.10em',
            color: 'var(--fg-dim, var(--cd-fg-muted))',
            opacity: 0.75,
            textTransform: 'lowercase',
          }}
        >
          v0 · mock
        </span>
      </div>
      <h3
        style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 500,
          letterSpacing: '0.01em',
          color: 'var(--fg, var(--cd-fg))',
        }}
      >
        {label}
      </h3>

      {/* Description */}
      <div
        style={{
          padding: '6px 0 12px',
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--fg-muted, var(--cd-fg-secondary))',
        }}
      >
        {description}
      </div>

      {/* 3-cell metric grid — empty (—) values, category-colored left border */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 8,
          marginBottom: 'auto',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            data-testid={`placeholder-metric-${i + 1}`}
            style={{
              border: '1px solid var(--border, var(--cd-border))',
              borderLeft: `2px solid ${visual.color}`,
              borderRadius: 2,
              padding: '8px 10px',
              opacity: 0.6,
            }}
          >
            <div
              className="mono"
              style={{
                fontSize: 8.5,
                color: 'var(--fg-dim, var(--cd-fg-muted))',
                letterSpacing: '0.08em',
                marginBottom: 4,
                textTransform: 'uppercase',
              }}
            >
              METRIC {i + 1}
            </div>
            <div
              className="mono tabular"
              style={{
                fontSize: 14,
                color: 'var(--fg-muted, var(--cd-fg-secondary))',
              }}
            >
              —
            </div>
          </div>
        ))}
      </div>

      {/* Footer: MODULE LIBRARY · not yet implemented */}
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--fg-dim, var(--cd-fg-muted))',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          paddingTop: 8,
          marginTop: 8,
          borderTop: '1px dashed var(--border, var(--cd-border))',
        }}
      >
        Module library · not yet implemented
      </div>
    </div>
  );
}

/** Default export so `React.lazy(() => import('./PlaceholderModule'))` works. */
export default PlaceholderModule;
